// @flow

import * as React from 'react';
import Hidden from '@material-ui/core/Hidden';
import Paper from '@material-ui/core/Paper';
import { withStyles } from '@material-ui/core/styles';

import { applyPageVersions, getRandomApplyPageVariation } from '../util/apply-page-testing';
import { logGAEvent } from '../util/log-ga';
import { initGoogleJobs } from '../util/google-jobs';

const styles = () => ({
  paper: {
    margin: '20px auto',
    padding: '25px',
    maxWidth: '800px',
    width: '100%',
  },
  applySection: {
    textAlign: 'right',
  },
});

type Props = CoreApplyProps & {
  classes: Object,
  job: Job,
  org: ?Org,
  logApplicationVisit: Function,
}

type State = {
  selectedPage: ?{
    key: string,
    component: Object,
    weight: number,
  },
  visitId: ?number,
}

class ApplyPage extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      // NOTE: The way this is written, it assumes that either the version is
      //  on the component when the component was created, or it'll pick
      //  a random component. Currently, this is fine as the version is
      //  only ever read of the URL, but in the future if the version is
      //  dictated by the server (or otherwise async) then this will
      //  need to be moved to getDerivedState
      selectedPage: null,
      visitId: null,
    };
  }

  componentDidMount = async () => {
    const { job, org } = this.props;
    if (job.status === 'PUBLISHED') {
      initGoogleJobs({ job, org });
    }
    this.setState({
      selectedPage: await this.getVersion(),
    });
  };

  getVersion = async () => {
    const { job, version } = this.props;

    // If there isn't a short description on the job, always send them
    //  to the SEO optimized page, which includes the full description.
    let selectedPage;
    if (!version && !job.shortDescription) {
      selectedPage = applyPageVersions.find(v => v.key.toLocaleLowerCase() === 's');
    } else if (version) {
      selectedPage = applyPageVersions.find(v => v.key.toLocaleLowerCase() === version.toLocaleLowerCase());
    } else {
      selectedPage = getRandomApplyPageVariation();
    }

    /*
    This can happen if someone has explicitly selected a version that does not
    exist. In this case, just give them a random one.
     */
    if (!selectedPage) {
      selectedPage = getRandomApplyPageVariation();
    }
    logGAEvent('Application', `Viewed Apply Page Version ${selectedPage.key}`);
    this.props.logApplicationVisit(selectedPage.key);
    return selectedPage;
  };

  jobCompensation = (job: Job) => {
    if (!job.shouldPublishCompensation || (!job.compensationHigh && !job.compensationLow)) {
      return null;
    }

    return { compensationHigh: job.compensationHigh, compensationLow: job.compensationLow, compensationType: job.compensationType };
  }

  render() {
    const {
      job,
      org,
      classes,
    } = this.props;
    const {
      selectedPage,
    } = this.state;

    const configuredApplyPage = selectedPage ? (
      <selectedPage.component
        job={job}
        jobCompensation={this.jobCompensation(job)}
        org={org}
      />
    ) : null;

    return (
      <React.Fragment>
        <Hidden smUp>
          {configuredApplyPage}
        </Hidden>
        <Hidden xsDown>
          <Paper className={classes.paper}>
            {configuredApplyPage}
          </Paper>
        </Hidden>
      </React.Fragment>
    );
  }
}

export default withStyles(styles)(ApplyPage);
